import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useDynamicFilter from "../../hooks/useDynamicFilter";

import { toTitleCase } from "../../util/stringUtils";

const ComboBox = (props) => {
  const {
    data,
    selected,
    valueKey,
    placeholder,
    searchTopic,
    formatData,
    handleSelect,
    handleNewItem,
    onRequestClose,
    inline = false,
    allowMultiple = false,
    maxLength,
    createNewMatchKey,
    sortKey,
    extraMatchData,
  } = props;

  const [itemIndex, setItemIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  const rawFilteredData = useDynamicFilter(data, searchTerm);
  const filteredData = useMemo(() => {
    if (!sortKey) return rawFilteredData;
    return [...rawFilteredData].sort((a, b) =>
      (a[sortKey] || "").localeCompare(b[sortKey] || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [rawFilteredData, sortKey]);

  const hasExactMatch =
    createNewMatchKey &&
    searchTerm.trim() &&
    [...data, ...(extraMatchData || [])].some(
      (item) =>
        (item[createNewMatchKey] || "").toLowerCase().trim() ===
        searchTerm.toLowerCase().trim()
    );
  const showCreateNewOption =
    !hasExactMatch &&
    (filteredData.length === 0 || (createNewMatchKey && searchTerm.trim()));

  const handleOptionClick = (item) => {
    handleSelect(item);
    setSearchTerm("");
    if (!allowMultiple) {
      onRequestClose();
    }
  };

  const renderNewItem = () => {
    if (handleNewItem) {
      return (
        <li
          className="new-tag"
          onClick={() => {
            handleNewItem(searchTerm);
            setSearchTerm("");
            if (!allowMultiple) onRequestClose();
          }}
        >
          + New {toTitleCase(searchTopic)} {searchTerm}
        </li>
      );
    } else {
      return <li className="no-results">No Results</li>;
    }
  };

  const createOptionFirst =
    showCreateNewOption && handleNewItem && filteredData.length > 0;
  const createOptionIndex = createOptionFirst ? 0 : filteredData.length;

  const renderData = () => {
    return filteredData
      .filter(
        (item) =>
          item.name !== "Blocking" &&
          item.name !== "Blocked" &&
          item.name !== "blocking" &&
          item.name !== "blocked"
      )
      .map((item, index) => {
        const isSelected = selected?.some(
          (selectedItem) => item[valueKey] === selectedItem[valueKey]
        );
        const listIndex = createOptionFirst ? index + 1 : index;

        return (
          <li
            key={item[valueKey]}
            onClick={() => handleOptionClick(item[valueKey])}
            className={`option ${isSelected ? "selected" : ""}`}
          >
            {formatData(item)}
          </li>
        );
      });
  };

  const totalOptions =
    filteredData.length + (showCreateNewOption && handleNewItem ? 1 : 0);
  const isCreateOptionFocused =
    showCreateNewOption && itemIndex === createOptionIndex;

  const handleKeyAction = (e) => {
    e.preventDefault();

    if (e.key === "Enter") {
      if (
        showCreateNewOption &&
        handleNewItem &&
        (filteredData.length === 0 || isCreateOptionFocused)
      ) {
        handleNewItem(searchTerm);
        setSearchTerm("");
        if (!allowMultiple) onRequestClose();
      } else if (filteredData.length > 0 && !isCreateOptionFocused) {
        const filteredIndex = createOptionFirst ? itemIndex - 1 : itemIndex;
        handleSelect(filteredData[filteredIndex][valueKey]);
        setSearchTerm("");
        if (!allowMultiple) {
          onRequestClose();
        }
      }
    } else if (e.key === "Escape") {
      onRequestClose();
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      setItemIndex((previous) => {
        const maxIndex = totalOptions - 1;
        if (e.key === "ArrowUp") {
          return previous === 0 ? maxIndex : previous - 1;
        }
        if (e.key === "ArrowDown") {
          return previous === maxIndex ? 0 : previous + 1;
        }
        return previous;
      });
    }
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    const listElements = listRef?.current;
    const itemElement = listElements?.querySelectorAll?.("li")?.[itemIndex];
    if (itemElement) {
      itemElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [itemIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        onRequestClose();
      }
    };

    const delay = allowMultiple ? 150 : 0;
    const id = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, delay);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onRequestClose, allowMultiple]);

  return (
    <div
      className={`combo-box-container ${inline ? "inline" : ""}`}
      ref={containerRef}
    >
      <div className={`search-bar-wrapper ${inline ? "inline" : ""}`}>
        <div className="magnifying-glass-icon">
          <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
        </div>
        <input
          ref={inputRef}
          type="search"
          id={`combo-search-${searchTopic || "default"}`}
          name={`combo-search-${searchTopic || "default"}`}
          className={`combo-search ${inline ? "inline" : ""}`}
          placeholder={placeholder}
          onKeyUp={handleKeyAction}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          {...(maxLength != null && { maxLength })}
        />
      </div>

      <ul
        ref={listRef}
        className={`combo-box-list-wrapper ${inline ? "inline" : ""}`}
      >
        {createOptionFirst && renderNewItem()}
        {renderData()}
        {showCreateNewOption && !createOptionFirst && renderNewItem()}
      </ul>
    </div>
  );
};

export default ComboBox;
